import {
  Alert,
  AlertDescription,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@design-system';
import { DashboardPageHeader } from '@/components/DashboardPageHeader';
import { OverviewTab } from '@client/features/wizard-advisors-metrics/components/overview-tab';
import { AlertCircle } from 'lucide-react';
import { AdvisorsMetricsTable } from '@client/features/wizard-advisors-metrics/components/advisors-metrics-table';
import { useSearchParams } from 'react-router-dom';
import { UsageAnalyticsView } from '../../../pages/AdvisorUsageAnalytics';
import { useAdvisorMetrics } from '../hooks/use-advisor-metrics';
import { useAdvisorUsageMetrics } from '../hooks/use-advisor-usage-metrics';
import { SubscriptionDetailsTable } from './subscription-details-table';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { useFeatureFlags } from '@client/contexts/FeatureFlagsContext';
import { useState } from 'react';
import type { MetricsOrganization } from '../types/metrics-type';
import { useNylMetricsApi, useNylMetricsExport } from '../hooks/api/use-advisor-metrics-api';
import { useDateFilter } from '../hooks/use-date-filter';
import { format } from 'date-fns';
import { EnhancedAdvisorUsageTable } from './enhanced-advisor-usage-table';

export function AdvisorMetricsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'overview';
  const filter = searchParams.get('filter');
  const { flags } = useFeatureFlags();
  const [overviewOrganization, setOverviewOrganization] = useState<MetricsOrganization>('all');
  const [usageOrganization, setUsageOrganization] = useState<MetricsOrganization>('all');
  const metrics = useAdvisorMetrics(overviewOrganization);
  const usageMetricsModel = useAdvisorMetrics(usageOrganization);
  const {
    data: {
      misc: { isLoading, error },
    },
    actions: { getFilteredAdvisors, getFilteredSubscriptions },
  } = metrics;
  const {
    data: { usageMetrics },
    actions: { getFilteredAdvisorsForUsage },
  } = usageMetricsModel;
  const { dateFilter: overviewDateFilter } = metrics.data.misc;
  const usageDateFilter = useDateFilter();
  const nylMetricsEnabled = flags.nylMetrics === true;
  const loadNylOverviewMetrics = nylMetricsEnabled && activeTab === 'overview';
  const loadNylUsageMetrics = nylMetricsEnabled && activeTab === 'usage';
  const overviewNylMetricsQuery = useNylMetricsApi({
    enabled: loadNylOverviewMetrics,
    scope: overviewOrganization,
    ...(overviewDateFilter.preset === 'allTime'
      ? {}
      : {
          from: format(overviewDateFilter.startDate, 'yyyy-MM-dd'),
          to: format(overviewDateFilter.endDate, 'yyyy-MM-dd'),
        }),
  });
  const overviewNylAllTimeMetricsQuery = useNylMetricsApi({
    enabled: loadNylOverviewMetrics && overviewDateFilter.preset !== 'allTime',
    scope: overviewOrganization,
  });
  const overviewNylAllTimeMetrics =
    overviewDateFilter.preset === 'allTime'
      ? overviewNylMetricsQuery.data
      : overviewNylAllTimeMetricsQuery.data;
  const usageNylMetricsQuery = useNylMetricsApi({
    enabled: loadNylUsageMetrics,
    scope: usageOrganization,
    ...(usageDateFilter.dateFilter.preset === 'allTime'
      ? {}
      : {
          from: format(usageDateFilter.dateFilter.startDate, 'yyyy-MM-dd'),
          to: format(usageDateFilter.dateFilter.endDate, 'yyyy-MM-dd'),
        }),
  });
  const nylMetricsExport = useNylMetricsExport(nylMetricsEnabled && usageOrganization === 'nyl', {
    scope: 'nyl',
    ...(usageDateFilter.dateFilter.preset === 'allTime'
      ? {}
      : {
          from: format(usageDateFilter.dateFilter.startDate, 'yyyy-MM-dd'),
          to: format(usageDateFilter.dateFilter.endDate, 'yyyy-MM-dd'),
        }),
  });
  const { advisorsList, activeAdvisorsCount, calculatedMetrics } = useAdvisorUsageMetrics(
    usageMetrics?.usageDetails || {},
    getFilteredAdvisorsForUsage,
  );
  const advisorDetailsById = new Map(
    getFilteredAdvisorsForUsage(searchParams.get('usageFilter')).map(advisor => [
      advisor.advisorId,
      advisor,
    ]),
  );
  const wizardAdvisorsList = advisorsList.map(advisor => {
    const advisorDetails = advisorDetailsById.get(advisor.advisorId);
    if (!advisorDetails) return advisor;

    const enrolledAt = advisorDetails.advisorCreatedDateTime;
    const enrolledAtTime = new Date(enrolledAt).getTime();
    const firstUseAt = advisorDetails.firstOpen;
    const firstUseAtTime = firstUseAt ? new Date(firstUseAt).getTime() : null;
    const timeToFirstUseSeconds =
      firstUseAtTime !== null && Number.isFinite(enrolledAtTime) && Number.isFinite(firstUseAtTime)
        ? Math.max(0, Math.round((firstUseAtTime - enrolledAtTime) / 1000))
        : null;

    return {
      ...advisor,
      enrolledAt,
      firstUseAt,
      timeToFirstUseSeconds,
    };
  });

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Failed to load overview metrics. Please try again later.
        </AlertDescription>
      </Alert>
    );
  }

  if (isLoading) {
    return (
      <div className="px-4 pb-12 pt-3 sm:px-6 lg:px-8">
        <div className="mt-8 flex flex-col items-center justify-center gap-6">
          <LoadingSpinner size={60} />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 pb-12 pt-3 sm:px-6 lg:px-8">
      <DashboardPageHeader
        title="Advisor Metrics"
        description="Comprehensive analytics and insights across all advisors"
        className="items-start md:items-center"
      />

      {((loadNylOverviewMetrics &&
        (overviewNylMetricsQuery.error || overviewNylAllTimeMetricsQuery.error)) ||
        (loadNylUsageMetrics && usageNylMetricsQuery.error)) && (
        <Alert
          variant="destructive"
          className="mt-6"
        >
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Failed to load NYL metrics. Existing Wizard metrics remain available.
          </AlertDescription>
        </Alert>
      )}

      <Tabs
        value={activeTab}
        onValueChange={value => setSearchParams({ tab: value })}
        className="mt-8"
      >
        <TabsList className="grid h-auto w-full grid-cols-2 gap-1 sm:grid-cols-4 lg:w-auto">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="advisors">Advisors</TabsTrigger>
          <TabsTrigger value="subscriptions">Subscriptions</TabsTrigger>
          <TabsTrigger value="usage">Usage</TabsTrigger>
        </TabsList>

        <TabsContent
          value="overview"
          className="mt-6"
        >
          <OverviewTab
            metrics={metrics}
            nylMetrics={nylMetricsEnabled ? overviewNylMetricsQuery.data : undefined}
            nylAllTimeMetrics={nylMetricsEnabled ? overviewNylAllTimeMetrics : undefined}
            showNylMetrics={nylMetricsEnabled}
            isNylMetricsLoading={nylMetricsEnabled && overviewNylMetricsQuery.isLoading}
            isNylAllTimeMetricsLoading={
              nylMetricsEnabled && overviewNylAllTimeMetricsQuery.isLoading
            }
            organization={nylMetricsEnabled ? overviewOrganization : undefined}
            onOrganizationChange={nylMetricsEnabled ? setOverviewOrganization : undefined}
            onMetricClick={(tab: string, filterKey: string) => {
              setSearchParams({ tab, filter: filterKey });
            }}
          />
        </TabsContent>

        <TabsContent
          value="advisors"
          className="mt-6"
        >
          <AdvisorsMetricsTable
            getFilteredAdvisors={getFilteredAdvisors}
            isLoading={isLoading}
            filterKey={filter}
          />
        </TabsContent>

        <TabsContent
          value="subscriptions"
          className="mt-6"
        >
          <SubscriptionDetailsTable
            getFilteredSubscriptions={getFilteredSubscriptions}
            isLoading={isLoading}
            filterKey={filter}
          />
        </TabsContent>

        <TabsContent
          value="usage"
          className="mt-6"
        >
          {nylMetricsEnabled ? (
            <EnhancedAdvisorUsageTable
              advisors={wizardAdvisorsList}
              onRefresh={usageNylMetricsQuery.refetch}
              isRefreshing={isLoading || usageNylMetricsQuery.isFetching}
              nylMetrics={usageNylMetricsQuery.data}
              isNylMetricsLoading={usageNylMetricsQuery.isLoading}
              dateFilter={usageDateFilter.dateFilter}
              organization={usageOrganization}
              onOrganizationChange={setUsageOrganization}
              onDatePresetChange={usageDateFilter.setPreset}
              onCustomDatesChange={usageDateFilter.setCustomDates}
              onExportNylClients={nylMetricsExport.startExport}
              isNylExporting={nylMetricsExport.isExporting}
              calculatedMetrics={calculatedMetrics}
            />
          ) : (
            <UsageAnalyticsView
              activeAdvisorsCount={activeAdvisorsCount}
              isLoading={isLoading}
              calculatedMetrics={calculatedMetrics}
              advisorsList={wizardAdvisorsList}
            />
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
